import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "motion/react";
import { Globe, ChevronDown } from "lucide-react";

const langs = [
  { value: "en", label: "English", short: "EN" },
  { value: "ar", label: "العربية", short: "AR" },
] as const;

function useChangeLang() {
  const { i18n } = useTranslation();
  return (value: string) => {
    i18n.changeLanguage(value);
    localStorage.setItem("selected-lang", value);
  };
}

/**
 * Inline segmented switcher used inside the mobile menu. The mobile menu panel
 * animates its height and therefore clips overflow, so an absolutely positioned
 * dropdown would be cut off there — this variant stays in flow instead.
 */
function InlineLangSelector() {
  const { i18n } = useTranslation();
  const changeLang = useChangeLang();

  return (
    <div
      className="inline-flex items-center gap-1 p-1 rounded-full bg-gray-100"
      role="group"
      aria-label="Select language"
    >
      <Globe className="w-4 h-4 mx-1.5 text-brand/50" aria-hidden="true" />
      {langs.map((l) => (
        <button
          key={l.value}
          type="button"
          onClick={() => changeLang(l.value)}
          aria-pressed={i18n.language === l.value}
          className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
            i18n.language === l.value
              ? "bg-brand text-white"
              : "text-brand/70 hover:text-brand"
          }`}
        >
          {l.short}
        </button>
      ))}
    </div>
  );
}

function LangSelector({ variant = "dropdown" }: { variant?: "dropdown" | "inline" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = langs.find((l) => l.value === i18n.language) || langs[0];
  const changeLang = useChangeLang();

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (variant === "inline") return <InlineLangSelector />;

  const select = (value: string) => {
    changeLang(value);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-brand/70 hover:text-brand hover:bg-gray-100 transition-all duration-200 text-xs font-bold"
        aria-label="Select language"
        aria-expanded={open}
      >
        <Globe className="w-4 h-4" />
        {current.short}
        <ChevronDown
          className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full mt-1.5 end-0 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden w-32 z-50"
          >
            {langs.map((l) => (
              <button
                key={l.value}
                type="button"
                onClick={() => select(l.value)}
                className={`w-full text-start px-4 py-2.5 text-xs font-semibold transition-colors ${
                  i18n.language === l.value
                    ? "bg-brand text-white"
                    : "text-brand/70 hover:bg-gray-50 hover:text-brand"
                }`}
              >
                {l.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LangSelector;
